import { useState } from "react";
import { useAuth } from "./AuthProvider";

export default function LoginScreen({ theme }) {
  const { sendLoginEmail, error, logout, firebaseUser } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const T = theme;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    setSendError(null);
    const result = await sendLoginEmail(email);
    setSending(false);
    if (result.success) {
      setSent(true);
    } else {
      setSendError(result.error);
    }
  };

  // Signed in via Firebase but no Firestore profile — not authorised
  const noProfile = (error === "no-profile" || error === "disabled") && firebaseUser;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(145deg, ${T.sidebar}, ${T.sidebarEnd})`,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          padding: 20,
          animation: "fadeUp .6s ease",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              width: 60,
              height: 60,
              margin: "0 auto 14px",
              background: `linear-gradient(135deg, ${T.accent}, ${T.primary})`,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: T.sidebar,
            }}
          >
            <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2" />
              <path d="M7 2v20" />
              <path d="M21 15V2a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
            </svg>
          </div>
          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              color: T.sidebarText,
              fontSize: 30,
              margin: 0,
            }}
          >
            Lodge Dining
          </h1>
          <p style={{ color: T.sidebarMuted, margin: "6px 0 0", fontSize: 13 }}>
            After-Meeting Management System
          </p>
        </div>

        {/* Card */}
        <div
          style={{
            background: T.surface,
            borderRadius: 16,
            padding: 32,
            boxShadow: "0 20px 60px rgba(0,0,0,.4)",
          }}
        >
          {/* ─── State: Not authorised ──────────────────────────── */}
          {noProfile && (
            <div>
              <div
                style={{
                  background: "#FEF3C7",
                  border: "1px solid #F59E0B",
                  borderRadius: 10,
                  padding: "16px 18px",
                  marginBottom: 20,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: "#92400E", marginBottom: 4 }}>
                  {error === "disabled" ? "Account Disabled" : "Account Not Recognised"}
                </div>
                <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.5 }}>
                  {error === "disabled"
                    ? <>Your account (<strong>{firebaseUser.email}</strong>) has been disabled by an administrator. Please contact them if you believe this is an error.</>
                    : <>You've signed in as <strong>{firebaseUser.email}</strong>, but there is no account set up for this email address. Please contact your administrator to be added to the system.</>
                  }
                </div>
              </div>
              <button
                onClick={logout}
                style={{
                  width: "100%",
                  padding: "12px 20px",
                  borderRadius: 8,
                  border: `1px solid ${T.borderInput}`,
                  background: "transparent",
                  color: T.primary,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Sign Out & Try a Different Email
              </button>
            </div>
          )}

          {/* ─── State: Link sent ──────────────────────────────── */}
          {sent && !noProfile && (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  margin: "0 auto 16px",
                  background: "#D1FAE5",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#065F46" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 20,
                  color: T.text,
                  margin: "0 0 8px",
                }}
              >
                Check Your Email
              </h2>
              <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6, margin: "0 0 8px" }}>
                We've sent a sign-in link to:
              </p>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: T.text,
                  margin: "0 0 16px",
                }}
              >
                {email}
              </p>
              <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.5, margin: "0 0 20px" }}>
                Click the link in the email to sign in. It may take a minute to arrive.
                Check your spam folder if you don't see it.
              </p>
              <button
                onClick={() => {
                  setSent(false);
                  setEmail("");
                }}
                style={{
                  width: "100%",
                  padding: "12px 20px",
                  borderRadius: 8,
                  border: `1px solid ${T.borderInput}`,
                  background: "transparent",
                  color: T.primary,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Use a Different Email
              </button>
            </div>
          )}

          {/* ─── State: Email input form ───────────────────────── */}
          {!sent && !noProfile && (
            <form onSubmit={handleSend}>
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 20,
                  color: T.text,
                  margin: "0 0 6px",
                }}
              >
                Sign In
              </h2>
              <p style={{ fontSize: 13, color: T.textMuted, margin: "0 0 20px", lineHeight: 1.5 }}>
                Enter your email and we'll send you a secure sign-in link. No password needed.
              </p>

              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 700,
                  color: T.textMuted,
                  marginBottom: 5,
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="treasurer@yourlodge.co.uk"
                required
                autoFocus
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 8,
                  border: `1px solid ${T.borderInput}`,
                  fontSize: 15,
                  fontFamily: "'DM Sans', sans-serif",
                  background: T.surface,
                  color: T.text,
                  outline: "none",
                  boxSizing: "border-box",
                  marginBottom: 16,
                }}
              />

              {sendError && (
                <div
                  style={{
                    background: "#FEE2E2",
                    border: "1px solid #EF4444",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 16,
                    fontSize: 13,
                    color: "#991B1B",
                  }}
                >
                  {sendError}
                </div>
              )}

              {error === "link-failed" && (
                <div
                  style={{
                    background: "#FEE2E2",
                    border: "1px solid #EF4444",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 16,
                    fontSize: 13,
                    color: "#991B1B",
                  }}
                >
                  The sign-in link was invalid or has expired. Please request a new one.
                </div>
              )}

              <button
                type="submit"
                disabled={sending || !email}
                style={{
                  width: "100%",
                  padding: "13px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: T.primary,
                  color: "#FFF",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: sending ? "wait" : "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  opacity: sending || !email ? 0.6 : 1,
                  transition: "opacity 0.2s",
                }}
              >
                {sending ? "Sending…" : "Send Sign-In Link"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
